import commonSetting from '@intelligence/components/DownloadCommon/DownloadPage/configs/commonSetting';
import {
  metricTitleMap,
  pcConsoleGameMetric,
} from '@intelligence/components/DownloadCommon/DownloadPage/common/baseConfig';
import { getMobileDetailDownloadTitle } from '@intelligence/components/DownloadCommon/DownloadPage/common/baseSetting';
import {
  setGameTitle,
  setItem,
  setMetricTitle,
  setPercentForPCConsole,
  setTitleByCompanyShow,
  setTitleByPublisherClassShow,
  setTitleByDateMethod,
  setTitleByGenreShow,
  setTitleByPlatform,
  setTitleByRegionMethod,
  setTitleByReleaseDateShow,
  setTitleBySteamIdShow,
  setTitleBySteamTagsShow,
  setTitleByGameModeShow,
  setTitleByGameEngineShow,
  setTitleBySupportedPlatformsShow,
} from '@intelligence/components/DownloadCommon/DownloadPage/common/titleSettingAtom';
import {
  getFileFormatByKey,
  getPlatformByKey,
  getRegionValueByKey,
  setMetricsByList,
  setTimeSettingsByDateMethod,
  setStartAndEndByDateMethod,
} from '@intelligence/components/DownloadCommon/DownloadPage/common/summarySettingAtom';
import { TOPCHARTS_COMMON_DOWNLOAD_API_PC_CONSOLE } from '@intelligence/components/ExportTable/instance/common';
import {
  pipe,
  getRegionMarket,
} from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { formatSurgeMetricFetchParams } from '@intelligence/services/topcharts';
import { validateOrderFields } from '@intelligence/v3/utils/topcharts';
import { getPCConsoleSourceOrder } from '@intelligence/pages/TopCharts/_config/pcConsoleMetricOrdering';

/**
 * Keep TopCharts download header consistent with the table header.
 *
 * Note:
 * - The table uses i18n keys like `dbi_metric_vgi_price` whose English text includes "VGI".
 * - The base `metricTitleMap` may map `est_price` to "Steam Price($)" (without "VGI").
 * - We do NOT "force add" a "VGI " prefix programmatically; instead we map specific metric keys
 *   to the exact table header strings.
 */
const VGI_METRIC_TITLE_OVERRIDES: Record<string, string> = {
  pcu: 'VGI Steam PCU',
  acu: 'VGI Steam ACU',
  steamfollowers: 'Steam Followers',
  steamreviews: 'VGI Steam Reviews',
  est_revenue: 'VGI Steam Premium Revenue($)',
  est_cumulative_revenue: 'VGI Steam Cumulative Revenue($)',
  est_units: 'VGI Steam Units',
  est_cumulative_units: 'VGI Steam Cumulative Units',
  est_price: 'VGI Steam Price($)',
  weighted_price: 'VGI Steam Weighted Price($)',
  lifetime_weighted_price: 'VGI Steam Lifetime Weighted Price($)',
  wishlists: 'VGI Steam Daily Wishlist',
  wishlists_total: 'VGI Steam Lifetime Wishlist',
  playtime: 'VGI Steam Avg. Playtime(mins)',
};
const VGI_DOWNLOAD_METRICS = new Set(
  Object.keys(VGI_METRIC_TITLE_OVERRIDES).filter(
    metric => metric !== 'steamfollowers',
  ),
);

const newMetricMap = {
  ...metricTitleMap,
  ...pcConsoleGameMetric,
  ...VGI_METRIC_TITLE_OVERRIDES,
};
const sourceDownloadSegment: Record<string, string[]> = {
  mscience: [
    'All',
    'Full Game',
    'DLC: All Other',
    'DLC: Expansion',
    'DLC: Season Pass',
    'DLC: Battle Pass',
    'MTX',
    'Subscription',
  ],
  gsd: ['All', 'Game', 'Add-On', 'In-Game Currency'],
  vginsights: ['All'],
  newzoo: ['All'],
  newzoo_weight: ['All'],
  npd: ['All'],
};

// const DOWNLOAD_STEAM_FAMILY_SOURCE_ORDER: Record<string, number> = {
//   steam: 0,
//   alinea: 1,
//   gamalytic: 2,
//   vginsights: 3,
// };

const getDownloadDisplaySource = (source: string, metric: any): string => {
  if (typeof metric !== 'string') {
    return source;
  }
  if (source === 'source_steam' || metric === 'steamfollowers' || metric.startsWith('source_steam__')) {
    return 'steam';
  }
  if (source === 'steamcharts') {
    return 'vginsights';
  }
  if (metric.startsWith('alinea__')) {
    return 'alinea';
  }
  if (metric.startsWith('gamalytic__')) {
    return 'gamalytic';
  }
  if (source === 'steamData' && VGI_DOWNLOAD_METRICS.has(metric)) {
    return 'vginsights';
  }
  return source;
};

// const getDownloadDisplaySourceOrder = (
//   displaySource: string,
//   defaultOrder: number,
// ): number => DOWNLOAD_STEAM_FAMILY_SOURCE_ORDER[displaySource] ?? defaultOrder;

const getDownloadDisplayMetricOrder = (displaySource: string, source: string, metric: any): number => {
  if (typeof metric !== 'string') return 0;
  if (displaySource === 'steam') {
    if (source === 'source_steam' || metric.startsWith('source_steam__')) return 0;
    if (metric === 'steamfollowers') return 1;
  }
  if (displaySource === 'vginsights') {
    if (metric === 'pcu') return 0;
    if (metric === 'acu') return 1;
    if (metric === 'steamreviews') return 2;
    return 3;
  }
  return 0;
};

const getOrderedMetricEntries = (data: Record<string, any> = {}) => {
  const metrics = data.source_metrics || {};
  // const displaySourceOrder: Record<string, number> = {};
  const entries: {
    source: string;
    metric: any;
    displaySource: string;
    metricOrder: number;
    index: number;
  }[] = [];

  Object.keys(metrics).forEach((key) => {
    let currentMetrics = metrics[key] || [];
    if (
      key === 'metacritic' &&
      metrics[key][0] === 'meta_score' &&
      data.device_method === 'aggregated'
    ) {
      currentMetrics = [
        'meta_score_Nintendo Switch',
        'meta_score_PC',
        'meta_score_PlayStation 4',
        'meta_score_PlayStation 5',
        'meta_score_Wii U',
        'meta_score_Xbox One',
        'meta_score_Xbox Series X/S',
      ];
    }

    currentMetrics?.forEach((metric: any) => {
      const displaySource = getDownloadDisplaySource(key, metric);
      // if (displaySourceOrder[displaySource] === undefined) {
      //   displaySourceOrder[displaySource] = Object.keys(displaySourceOrder).length;
      // }
      entries.push({
        source: key,
        metric,
        displaySource,
        metricOrder: getDownloadDisplayMetricOrder(displaySource, key, metric),
        index: entries.length,
      });
    });
  });

  return entries.sort((a, b) => {
    // 与 PC&Console 指标选择器共用数据源顺序，同源仍保持原定义顺序。
    const sourceOrder =
      getPCConsoleSourceOrder(a.displaySource) - getPCConsoleSourceOrder(b.displaySource);
    if (sourceOrder !== 0) {
      return sourceOrder;
    }
    const metricOrder = a.metricOrder - b.metricOrder;
    if (metricOrder !== 0) {
      return metricOrder;
    }
    return a.index - b.index;
  });
};

const getOrderedSourceMetrics = (sourceMetrics: Record<string, any> = {}) => {
  const result: Record<string, any[]> = {};
  getOrderedMetricEntries({ source_metrics: sourceMetrics }).forEach(
    ({ source, metric, displaySource }) => {
      const key = `${displaySource}_${source}`;
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(metric);
    },
  );
  return result;
};

const reorderMetricEntriesByDownloadMetrics = (
  entries: { source: string; metric: any }[],
  downloadMetrics?: string[],
) => {
  if (!Array.isArray(downloadMetrics) || !downloadMetrics.length) {
    return entries;
  }
  const entryMap = new Map(
    entries.map(entry => [entry.metric, entry] as const),
  );
  const reordered = downloadMetrics
    .map(metric => entryMap.get(metric))
    .filter(Boolean) as { source: string; metric: any }[];
  reordered.sort(
    (a, b) =>
      getPCConsoleSourceOrder(getDownloadDisplaySource(a.source, a.metric)) -
      getPCConsoleSourceOrder(getDownloadDisplaySource(b.source, b.metric)),
  );
  // download_metrics 与 source_metrics 未对齐时回退，避免 Excel 列被清空导致无数据
  return reordered.length ? reordered : entries;
};

const getDownloadColumnOrder = (data: Record<string, any> = {}): string[] | undefined => {
  if (Array.isArray(data.order_metrics) && data.order_metrics.length) {
    return data.order_metrics
      .map((item: { metric?: string }) => item?.metric)
      .filter((metric): metric is string => Boolean(metric));
  }
  return data.download_metrics;
};

const mergeData = function (data: Record<string, any> = {}) {
  const result: { Metric: string; Segment?: string; Source: string }[] = [];
  const segments = sourceDownloadSegment;
  const metricEntries = reorderMetricEntriesByDownloadMetrics(
    getOrderedMetricEntries(data),
    getDownloadColumnOrder(data),
  );

  metricEntries.forEach(({ source, metric }) => {
    if (typeof metric === 'string' && metric.endsWith('_time')) {
      return;
    }

    const currentSegments = segments[source] || [];
    if (data.segment_method === 'aggregated') {
      result.push({ Metric: metric, Source: '' });
    } else {
      if (currentSegments?.length) {
        currentSegments?.forEach((segment: any) => {
          result.push({ Metric: metric, Segment: segment, Source: '' });
        });
      } else {
        result.push({ Metric: metric, Segment: '', Source: '' });
      }
    }
  });

  return result;
};
const getDownloadParams = (params: Record<string, any> = {}) => {
  const metricList = mergeData(params);
  const pipeFront = pipe(
    setGameTitle,
    setTitleByCompanyShow(params.company_show),
    setTitleByPublisherClassShow(params.publisher_class_show),
    setTitleByGenreShow(params.genre_show),
    setTitleBySteamIdShow(params.steam_id_show),
    setTitleBySteamTagsShow(params.steam_tag_show),
    setTitleByGameModeShow(params.game_mode_show),
    setTitleByGameEngineShow(params.game_engine_show),
    setTitleBySupportedPlatformsShow(params.platform_show),
    setTitleByReleaseDateShow(params.release_date_show),
    setTitleByDateMethod(params.downlaod_date_method),
    setTitleByRegionMethod(params.region_method),
    setTitleByPlatform(params.device_method),
  );
  const pipeInLoop = pipe(
    setItem,
    setMetricTitle(newMetricMap, params.date_type, 'isObject'),
    setPercentForPCConsole(params.ratio_show),
  );

  const excelTitleList = getMobileDetailDownloadTitle(pipeFront, metricList, pipeInLoop);
  const sheetNames = 'Stats';
  return {
    sheetName: [sheetNames],
    [sheetNames]: excelTitleList,
  };
};
// const getMarket = (params: any) => {
//   const { markets, regionOptions } = params || {};
//   if (!markets?.length) return undefined;
//   if (markets?.length && !markets.includes('global')) return markets;
//   const marketList = [];
//   for (const item of regionOptions) {
//     const itemMarket = item.country_set?.map((item: any) => item.country_code);
//     marketList.push(...itemMarket);
//   }
//   marketList.unshift('global');
//   return marketList.flat();
// };

const getFParams = (params: any) => {
  const { region_method, device_method, downlaod_date_method } = params;
  return {
    b_group_by_segment: false,
    b_group_by_market: region_method === 'market',
    b_group_by_platform: device_method === 'separate',
    b_group_by_device: device_method === 'device',
    b_group_by_region: region_method === 'region',
    b_group_by_date: downlaod_date_method !== 'aggregated',
    b_growth_ratio: params.ratio_show === 1 || params.ratio_show === '1',
    company_show: params?.company_show === 1 || params?.company_show === '1',
    publisher_class_show: params?.publisher_class_show === 1 || params?.publisher_class_show === '1',
    genre_show: params?.genre_show === 1 || params?.genre_show === '1',
    steam_id_show: params?.steam_id_show === 1 || params?.steam_id_show === '1',
    release_date_show:
      params?.release_date_show === 1 || params?.release_date_show === '1',
    game_engine_show:
      params?.game_engine_show === 1 || params?.game_engine_show === '1',
    segments: params?.segments || ['All'],
    markets:
      region_method === 'aggregated'
        ? params?.regions
        : getRegionMarket(params),
  };
};

const getDownloadApiParams = (params: any) => {
  const {
    region_method,
    device_method,
    segment_method,
    downlaod_date_method,
    tableSelection = [],
    select_top,
    entity_type,
    fp = {},
    source_metrics,
  } = params;
  const { regions = [], date_type, end_date, start_date, device, segment } = fp;

  // console.log('paramsparamsaadfasd', params);
  const isSelected =
    Array.isArray(params?.select_ids) && params?.select_ids?.length > 0;

  // 优先使用下载弹窗传递的 source_metrics，避免页面级别的值混入
  const finalSourceMetrics = source_metrics || fp?.source_metrics || {};
  // 确保 order_source/order_metric 在最终 source_metrics 中存在，否则回退到第一个
  const { orderSource: validatedOrderSource, orderMetric: validatedOrderMetric } = validateOrderFields(finalSourceMetrics, fp?.order_source, fp?.order_metric);

  // 构建下载参数
  // 搜索/筛选生效时（fp.unified_ids 非空），download_params.ids 需与外层 unified_ids 保持一致，
  // 避免使用搜索前残留的勾选行（select_ids）导致下载游戏集合与当前筛选不一致；
  // 无搜索时回退到 select_ids，保留 checkedLines / selectTop 的勾选下载语义。
  let downloadIds: any[] = [];
  if (Array.isArray(fp?.unified_ids) && fp.unified_ids.length > 0) {
    downloadIds = fp.unified_ids;
  } else if (isSelected) {
    downloadIds = params?.select_ids;
  }
  const apiParams = {
    ...fp,
    source_metrics: finalSourceMetrics,
    order_source: validatedOrderSource,
    order_metric: validatedOrderMetric,
    page: 1,
    page_size: select_top > 500 ? 500 : select_top,
    metric_params: {
      mode: 'download',
      download_params: {
        metric_condition: fp.metric_condition,
        ids: downloadIds,
        entity_type,
        markets:
          region_method === 'aggregated'
            ? params?.regions
            : getRegionMarket(params),
        granularity: date_type,
        start_date,
        end_date,
        platforms: device,
        source_metrics: finalSourceMetrics,
        segments: segment ? [segment] : ['All'],
        start_release_time: 0,
        end_release_time: 0,
        b_group_by_segment: false, // 此处后端未定义，暂时与b_group_by_device一样
        b_group_by_market: region_method === 'market',
        b_group_by_platform: device_method === 'separate',
        b_group_by_device: device_method === 'device',
        b_group_by_region: region_method === 'region',
        b_group_by_date: downlaod_date_method !== 'aggregated',
        b_growth_ratio: params.ratio_show === 1 || params.ratio_show === '1',
        b_download: true,
        company_show:
          params?.company_show === 1 || params?.company_show === '1',
        genre_show: params?.genre_show === 1 || params?.genre_show === '1',
        steam_id_show:
          params?.steam_id_show === 1 || params?.steam_id_show === '1',
        release_date_show:
          params?.release_date_show === 1 || params?.release_date_show === '1',
        game_engine_show:
          params?.game_engine_show === 1 || params?.game_engine_show === '1',
      },
    },
    // markets: region_method == 'aggregated' ? params?.markets : getMarket(params),
  };

  // 处理异动指标参数
  return formatSurgeMetricFetchParams(apiParams);
};

const getSummarySheet = (params: Record<string, any>) => {
  const list: any[] = [];
  const { region_method, file_format, source_metrics, downlaod_date_method } =
    params || {};
  const orderedSourceMetrics = getOrderedSourceMetrics(source_metrics);
  const setSummarySheet = pipe(
    getPlatformByKey(params),
    setStartAndEndByDateMethod(params),
    setMetricsByList(orderedSourceMetrics, newMetricMap),
    setTimeSettingsByDateMethod(downlaod_date_method),
    getRegionValueByKey(region_method),
    getFileFormatByKey(file_format),
  );
  setSummarySheet(list);
  return list;
};
const granularityObject: Record<string, string> = {
  daily: 'D',
  weekly: 'W',
  monthly: 'M',
};
const getFileName = (params: any) => {
  const {
    start_release_time,
    start_date,
    end_release_time,
    end_date,
    granularity,
    new_style_donwload_file_name,
  } = params;
  const gItem = granularityObject[granularity];
  if (start_date && end_date) {
    return `${new_style_donwload_file_name}_${start_date}_${end_date}`;
  }
  if (start_release_time && end_release_time) {
    return `${new_style_donwload_file_name}_Release_${gItem}${start_release_time}_${gItem}${end_release_time}`;
  }
  return `${new_style_donwload_file_name}`;
};
export default (params: Record<string, any>) => ({
  ...commonSetting,
  isUsingNewMethod: false,
  $$fileName: getFileName(params),
  downloadFetchApi: TOPCHARTS_COMMON_DOWNLOAD_API_PC_CONSOLE,
  dataKey: undefined,
  summarySheet: getSummarySheet(params),
  regionOptions: params?.regionOptions,
  byDate: params?.downlaod_date_method !== 'aggregated',
  granularity: granularityObject[params?.granularity],
  isRelease: !!(params?.end_release_time && params?.start_release_time),
  fParams: getFParams(params),
  downloadApiParams: getDownloadApiParams(params),
  fetchOnlyOnce: false,
  ...getDownloadParams(params),
});
